<script lang="ts" setup>
import { HeaderWrapper } from '@haskoning/vue-component-library'
import { storeToRefs } from 'pinia'
import AppDetailSideBar from '@/components/app/AppDetailSideBar.vue'
import { useProjectStore } from '@/stores/project'
import { computed } from 'vue'
import { useProjectUserStore } from '@/stores/projectUser'
import { useRouter } from 'vue-router'
import { useScenarioStore } from '@/stores/scenario'
import { navigationItems } from '@/components/app/constants/navigationItems'
import IdentityManager from '@arcgis/core/identity/IdentityManager'
import { useAuthorizationStore } from '@/stores/authorization'
import { useDjangoAuthStore } from '@/stores/authDjango'

const authStore = useDjangoAuthStore()
const router = useRouter()
const authorizationStore = useAuthorizationStore()

const { user } = storeToRefs(authStore)
const { writeLoading: projectWriteLoading, deleteLoading: projectDeleteLoading } =
  storeToRefs(useProjectStore())
const { writeLoading: projectUserWriteLoading, deleteLoading: projectUserDeleteLoading } =
  storeToRefs(useProjectUserStore())
const { writeLoading: scenarioWriteLoading, deleteLoading: scenarioDeleteLoading } =
  storeToRefs(useScenarioStore())

const loading = computed(() => {
  return (
    projectWriteLoading.value ||
    projectDeleteLoading.value ||
    projectUserWriteLoading.value ||
    projectUserDeleteLoading.value ||
    scenarioWriteLoading.value ||
    scenarioDeleteLoading.value
  )
})

const drawerStore = useDrawerStore()

drawerStore.drawer.shown = true
const { currentItem: currentProject } = storeToRefs(useProjectStore())
const { currentItem: currentScenario } = storeToRefs(useScenarioStore())

const route = useRoute()

const currentRouteName = computed(() => route.name)

const currentPageTitle = computed(() => {
  const currentItem = navigationItems.find((item) => item.event === route.name)
  return currentItem ? currentItem.title : 'My Projects'
})

const isSidebarPage = computed(() => {
  const routeName = currentRouteName.value
  return (
    routeName === 'project-index' ||
    routeName === 'how-to-get-started' ||
    routeName === 'about-spin' ||
    routeName === 'project-examples' ||
    routeName === 'contact-us'
  )
})

function arcigLogout() {
  if (IdentityManager) {
    IdentityManager.destroyCredentials()
    window.location.reload()
  }
}

async function logout() {
  await authStore.logout()
  await authStore.$patch({
    user: null
  })
  await arcigLogout()
  await authorizationStore.clearToken()
  await router.push({ name: 'auth-login' })
}
</script>

<template>
  <HeaderWrapper
    :title="'SPIN'"
    :user="user"
    image-src="/logo-haskoning-white.png"
    :loading="loading"
    menu-color="#FFFFFF"
    menu-text-color="#009eab"
    @logout="logout"
    v-if="user"
  >
    <template #toolbar-prepend>
      <v-app-bar-nav-icon
        class="pl-3 no-overlay"
        height="36"
        width="40"
        color="white"
        :ripple="false"
        :icon="drawerStore.drawer.shown ? 'mdi-close' : 'mdi-menu'"
        @click="drawerStore.toggleDrawer"
      />
    </template>
    <template #breadcrumbs-items>
      <!-- My Projects -->
      <v-breadcrumbs-item>
        <router-link
          class="text-white toolbar-text text-decoration-none"
          :to="{ name: 'project-index' }"
        >
          {{ currentPageTitle }}
        </router-link>
      </v-breadcrumbs-item>
      <!-- Project Name -->
      <template v-if="currentProject?.id && $route.name !== 'project-index'">
        <v-breadcrumbs-divider />
        <v-breadcrumbs-item>
          <router-link
            class="text-white toolbar-text text-decoration-none"
            :to="{ name: 'project-detail', params: { id: currentProject.id } }"
          >
            {{ currentProject?.name }}
          </router-link>
        </v-breadcrumbs-item>
      </template>
      <!-- Scenario Name -->
      <template v-if="currentScenario?.id && $route.name !== 'project-detail'">
        <v-breadcrumbs-divider />
        <v-breadcrumbs-item>
          {{ currentScenario?.name }}
        </v-breadcrumbs-item>
      </template>
    </template>
  </HeaderWrapper>
  <AppDetailSideBar v-if="isSidebarPage" />
</template>

<style scoped>
.no-overlay :deep(.v-btn__overlay) {
  display: none;
}
.title {
  font-size: 16px;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  letter-spacing: 1px;
}
</style>
